import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { User, Mail, Phone } from "lucide-react";
import { User as UserType } from "@nusrif/types";
import { createCart, getUser, updateUser } from "@nusrif/services";
import { useNavigate, useSearchParams } from "react-router";
import { clearHistoryAndNavigate } from "@nusrif/utils";
export type OnboardingDetails = Pick<
  UserType,
  "firstName" | "lastName" | "email" | "phoneNumber"
>;
export const OnboardingForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [details, setDetails] = useState<OnboardingDetails>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  const [prefilledFields, setPrefilledFields] = useState<
    Partial<Record<keyof OnboardingDetails, boolean>>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  }>({
    firstName: "Required",
    lastName: "Required",
    email: "Required",
    phoneNumber: "Required",
  });
  useEffect(() => {
    const user = getUser();
    if (user) {
      const initialDetails = {
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
      };
      setDetails(initialDetails);
      const prefilled: Partial<Record<keyof OnboardingDetails, boolean>> = {};
      (Object.keys(initialDetails) as Array<keyof OnboardingDetails>).forEach(
        (key) => {
          if (initialDetails[key]) {
            prefilled[key] = true;
          }
        }
      );
      setPrefilledFields(prefilled);
      // Clear errors for prefilled fields
      setErrors((prev) => ({
        ...prev,
        ...(prefilled.firstName && { firstName: "" }),
        ...(prefilled.lastName && { lastName: "" }),
        ...(prefilled.email && { email: "" }),
        ...(prefilled.phoneNumber && { phoneNumber: "" }),
      }));
    }
  }, []);
  const handleInputChange = (field: keyof OnboardingDetails, value: string) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
    if (!prefilledFields[field]) {
      if (value.trim() === "") {
        setErrors((prev) => ({ ...prev, [field]: "Required" }));
      } else if (field === "email") {
        if (!validateEmail(value)) {
          setErrors((prev) => ({ ...prev, email: "Invalid email address" }));
        } else {
          setErrors((prev) => ({ ...prev, email: "" }));
        }
      } else {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };
  const validateEmail = (email: string): boolean => {
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email) && email.length <= 254;
  };
  const allEditableFieldsValid = (): boolean => {
    return (Object.keys(errors) as Array<keyof OnboardingDetails>)
      .filter((key) => !prefilledFields[key])
      .every((key) => errors[key] === "");
  };
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await updateUser(details);
      console.log("User onboarding completed:", details);
      // Redirect to the return URL if provided
      const returnUrl = searchParams.get("returnUrl");
      const params = searchParams.toString(); // returns the query string without '?'
      const fullReturnUrl = returnUrl
        ? `${returnUrl}${params ? `?${params}` : ""}`
        : "/";
      console.log("Return URL:", fullReturnUrl);
      // Create cart
      if (response.success && !response.data.user?.cartId) {
        await createCart();
      }
      clearHistoryAndNavigate(navigate, fullReturnUrl || "/");
    } catch (error) {
      console.error("Error during onboarding:", error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="space-y-4">
      {/* First Name */}
      <Field
        label="First Name"
        icon={<User className="w-4 h-4 text-gray-400" />}
        value={details.firstName}
        error={errors.firstName}
        disabled={prefilledFields.firstName}
        onChange={(val) => handleInputChange("firstName", val)}
      />
      {/* Last Name */}
      <Field
        label="Last Name"
        icon={<User className="w-4 h-4 text-gray-400" />}
        value={details.lastName}
        error={errors.lastName}
        disabled={prefilledFields.lastName}
        onChange={(val) => handleInputChange("lastName", val)}
      />
      {/* Email */}
      <Field
        label="Email"
        icon={<Mail className="w-4 h-4 text-gray-400" />}
        value={details.email}
        error={errors.email}
        disabled={prefilledFields.email}
        onChange={(val) => handleInputChange("email", val)}
      />
      {/* Phone */}
      <Field
        label="Phone Number"
        icon={<Phone className="w-4 h-4 text-gray-400" />}
        value={details.phoneNumber}
        error={errors.phoneNumber}
        disabled={prefilledFields.phoneNumber}
        onChange={(val) => handleInputChange("phoneNumber", val)}
      />
      {/* Submit Button */}
      <div className="flex pt-6">
        <Button
          onClick={handleSubmit}
          className="flex-1 h-12"
          disabled={isLoading || !allEditableFieldsValid()}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            "Complete Setup"
          )}
        </Button>
      </div>
    </div>
  );
};
const Field = ({
  label,
  icon,
  value,
  error,
  disabled,
  onChange,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  error: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2">{icon}</div>
      <Input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`pl-10 h-12 border-gray-300 ${
          error ? "border-red-500" : ""
        }`}
      />
    </div>
    {!disabled && error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);
